type SanitizeInlineSvgOptions = {
	idPrefix: string;
	ariaLabel?: string;
	disableAnimation?: boolean;
};

const allowedElements = new Set([
	'svg',
	'g',
	'path',
	'circle',
	'rect',
	'line',
	'polyline',
	'polygon',
	'ellipse',
	'text',
	'tspan',
	'title',
	'desc',
	'defs',
	'filter',
	'fedropshadow',
	'fegaussianblur',
	'feoffset',
	'feflood',
	'fecolormatrix',
	'fecomposite',
	'feblend',
	'femerge',
	'femergenode',
	'femorphology',
	'lineargradient',
	'radialgradient',
	'stop',
	'clippath',
	'mask',
	'pattern',
	'symbol',
	'use',
	'marker',
	'mpath',
	'animate',
	'animatetransform',
	'animatemotion',
]);

const animationElements = new Set(['animate', 'animatetransform', 'animatemotion', 'mpath']);
const blockedElements = new Set(['script', 'foreignobject', 'iframe', 'object', 'embed', 'style']);

const allowedAttributes = new Set([
	'id',
	'class',
	'style',
	'xmlns',
	'xmlns:xlink',
	'viewbox',
	'preserveaspectratio',
	'width',
	'height',
	'x',
	'y',
	'x1',
	'x2',
	'y1',
	'y2',
	'cx',
	'cy',
	'r',
	'rx',
	'ry',
	'd',
	'points',
	'path',
	'transform',
	'origin',
	'fill',
	'fill-opacity',
	'fill-rule',
	'stroke',
	'stroke-width',
	'stroke-linecap',
	'stroke-linejoin',
	'stroke-miterlimit',
	'stroke-dasharray',
	'stroke-dashoffset',
	'stroke-opacity',
	'opacity',
	'clip-path',
	'clip-rule',
	'mask',
	'filter',
	'marker-start',
	'marker-mid',
	'marker-end',
	'markerwidth',
	'markerheight',
	'markerunits',
	'refx',
	'refy',
	'orient',
	'gradientunits',
	'gradienttransform',
	'spreadmethod',
	'offset',
	'stop-color',
	'stop-opacity',
	'font-family',
	'font-size',
	'font-style',
	'font-weight',
	'letter-spacing',
	'word-spacing',
	'text-anchor',
	'dominant-baseline',
	'alignment-baseline',
	'dy',
	'dx',
	'rotate',
	'href',
	'xlink:href',
	'attributeName'.toLowerCase(),
	'attributetype',
	'type',
	'from',
	'to',
	'by',
	'values',
	'dur',
	'begin',
	'end',
	'min',
	'max',
	'restart',
	'repeatcount',
	'repeatdur',
	'calcmode',
	'keytimes',
	'keysplines',
	'keypoints',
	'additive',
	'accumulate',
	'fr',
	'fx',
	'fy',
	'stddeviation',
	'flood-color',
	'flood-opacity',
	'result',
	'in',
	'in2',
	'mode',
	'operator',
	'focusable',
]);

export function sanitizeInlineSvg(rawSvg: string, options: SanitizeInlineSvgOptions): string | null {
	if (!rawSvg.trim() || typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
		return null;
	}

	const parser = new DOMParser();
	const parsed = parser.parseFromString(rawSvg, 'image/svg+xml');

	if (parsed.querySelector('parsererror')) {
		return null;
	}

	const root = parsed.documentElement;
	if (!root || root.localName.toLowerCase() !== 'svg') {
		return null;
	}

	sanitizeElement(root, true, options.disableAnimation === true);
	const idMap = prefixIds(root, options.idPrefix);
	rewriteIdReferences(root, idMap);
	applyAccessibility(root, options.ariaLabel);

	return new XMLSerializer().serializeToString(root);
}

function sanitizeElement(element: Element, isRoot: boolean, disableAnimation: boolean): void {
	const tagName = element.localName.toLowerCase();
	if ((!isRoot && blockedElements.has(tagName)) || !allowedElements.has(tagName) || (disableAnimation && animationElements.has(tagName))) {
		element.remove();
		return;
	}

	for (const attr of Array.from(element.attributes)) {
		if (!isSafeAttribute(attr.name, attr.value)) {
			element.removeAttribute(attr.name);
		}
	}

	for (const child of Array.from(element.children)) {
		sanitizeElement(child, false, disableAnimation);
	}
}

function isSafeAttribute(name: string, value: string): boolean {
	const normalizedName = name.toLowerCase();
	if (normalizedName.startsWith('on')) return false;
	if (normalizedName.startsWith('aria-') || normalizedName === 'role') return false;
	if (!allowedAttributes.has(normalizedName)) return false;
	if (normalizedName === 'xmlns' || normalizedName === 'xmlns:xlink') return true;

	const normalizedValue = stripControlChars(value).trim().toLowerCase();
	if (normalizedValue.includes('javascript:') || normalizedValue.includes('vbscript:')) return false;

	if (normalizedName === 'href' || normalizedName === 'xlink:href' || normalizedName === 'src') {
		return value.trim().startsWith('#');
	}

	return !containsExternalUrl(value);
}

function stripControlChars(value: string): string {
	return Array.from(value)
		.filter((char) => {
			const code = char.charCodeAt(0);
			return code > 31 && (code < 127 || code > 159);
		})
		.join('');
}

function containsExternalUrl(value: string): boolean {
	const urlMatches = value.matchAll(/url\(\s*(['"]?)(.*?)\1\s*\)/gi);
	for (const match of urlMatches) {
		const urlValue = match[2]?.trim() ?? '';
		if (!urlValue.startsWith('#')) {
			return true;
		}
	}

	return /(?:https?:|data:|file:|blob:|\/\/)/i.test(value);
}

function prefixIds(root: Element, rawPrefix: string): Map<string, string> {
	const idMap = new Map<string, string>();
	const prefix = rawPrefix.replace(/[^a-zA-Z0-9_-]/g, '-');

	for (const element of Array.from(root.querySelectorAll('[id]'))) {
		const id = element.getAttribute('id');
		if (!id) continue;

		const prefixedId = `${prefix}-${id}`;
		idMap.set(id, prefixedId);
		element.setAttribute('id', prefixedId);
	}

	const rootId = root.getAttribute('id');
	if (rootId) {
		const prefixedRootId = `${prefix}-${rootId}`;
		idMap.set(rootId, prefixedRootId);
		root.setAttribute('id', prefixedRootId);
	}

	return idMap;
}

function rewriteIdReferences(root: Element, idMap: Map<string, string>): void {
	if (idMap.size === 0) return;

	for (const element of [root, ...Array.from(root.querySelectorAll('*'))]) {
		for (const attr of Array.from(element.attributes)) {
			const rewritten = rewriteReferenceValue(attr.value, idMap);
			if (rewritten !== attr.value) {
				element.setAttribute(attr.name, rewritten);
			}
		}
	}
}

function rewriteReferenceValue(value: string, idMap: Map<string, string>): string {
	let rewritten = value.replace(/url\(\s*(['"]?)#([^)'" \t]+)\1\s*\)/g, (match, quote: string, id: string) => {
		const mapped = idMap.get(id);
		return mapped ? `url(${quote}#${mapped}${quote})` : match;
	});

	rewritten = rewritten.replace(/^#([A-Za-z][\w:.-]*)$/, (match, id: string) => {
		const mapped = idMap.get(id);
		return mapped ? `#${mapped}` : match;
	});

	return rewritten;
}

function applyAccessibility(root: Element, ariaLabel: string | undefined): void {
	root.removeAttribute('aria-label');
	root.removeAttribute('aria-hidden');
	root.removeAttribute('aria-labelledby');
	root.removeAttribute('aria-describedby');
	root.removeAttribute('role');

	const label = ariaLabel?.trim();
	if (label) {
		root.setAttribute('role', 'img');
		root.setAttribute('aria-label', label);
		root.setAttribute('focusable', 'false');
		return;
	}

	root.setAttribute('aria-hidden', 'true');
	root.setAttribute('focusable', 'false');
}
